// SessionSidebar.tsx — the Sessions page's left pane: a slim action row
// (New workspace) above the scrollable list of workspaces, each heading
// the sessions that run on it.
//
// The list header carries exactly one control: a new session on a new
// workspace. A session on an existing workspace starts from that
// workspace's own header. Everything that configures a conversation
// (tools, model) lives in the composer — the list is for picking a
// session, nothing else.
//
// Within a workspace, a forked session is drawn under the session it was
// forked from (sessionTree.ts), behind a left rule per level, and a parent
// row's chevron collapses its forks. Forks start expanded only when one
// of them is running or selected — otherwise a swarm's worth of branches
// would bury the list.
//
// Owns the clock the rows' recency badges print against, and the user's
// expand/collapse choices.
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner, Stack } from 'react-bootstrap';
import { BsPlusCircle } from 'react-icons/bs';
import type { Session, Workspace } from '../api';
import { activityOf } from './live/sessionLive';
import type { LiveSnapshot } from './live/sessionLive';
import SessionRow from './SessionRow';
import { turnInFlight } from './sessionFormat';
import { buildSessionTree, countDescendants, MAX_FORK_INDENT, someDescendant } from './sessionTree';
import type { SessionTreeNode } from './sessionTree';
import WorkspaceHeader from './WorkspaceHeader';
import type { WorkspaceGroup } from './workspaceGroups';

export interface SessionSidebarProps {
  width: number;
  groups: WorkspaceGroup<Session, Workspace>[];
  selectedId: string | null;
  live: LiveSnapshot;
  titleDrafts: Record<string, string>;
  /** A create in flight: 'new' for New workspace, else the workspace id. */
  creating: string | null;
  onToggleSelect: (id: string) => void;
  onDraftChange: (id: string, value: string) => void;
  onDraftClear: (id: string) => void;
  onNewSession: (workspaceId?: string) => void;
  onRenameSession: (id: string, name: string) => void;
  onAutoTitle: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onRenameWorkspace: (id: string, name: string) => void;
  onDeleteWorkspace: (ws: Workspace, sessionCount: number) => void;
}

interface BranchProps {
  node: SessionTreeNode<Session>;
  /** The parent's title, for a fork's accessible name. */
  forkOf?: string;
  now: number;
  /** The user's expand/collapse choices, by parent session id. */
  forkOpen: Record<string, boolean>;
  setForkOpen: (id: string, open: boolean) => void;
  sidebar: SessionSidebarProps;
}

// SessionBranch draws one session and, when expanded, its forks beneath
// it. Each level up to MAX_FORK_INDENT steps in behind a left rule (the
// theme's border color); deeper forks line up at the cap rather than
// squeezing titles to nothing — their accessible name still says whose
// fork they are.
const SessionBranch: React.FC<BranchProps> = ({ node, forkOf, now, forkOpen, setForkOpen, sidebar: p }) => {
  const s = node.session;
  const title = s.name || 'New session';
  const count = countDescendants(node);
  // Expanded by default when the forks hold something the user would
  // otherwise lose sight of: a running turn, or the selected session.
  const wantOpen = someDescendant(node, (c) =>
    c.id === p.selectedId || turnInFlight(activityOf(p.live, c.id).status));
  const expanded = count > 0 && (forkOpen[s.id] ?? wantOpen);
  const groupId = `forks-${s.id}`;
  const indents = node.indent < MAX_FORK_INDENT;
  return (
    <>
      <SessionRow
        session={s}
        isActive={s.id === p.selectedId}
        draft={p.titleDrafts[s.id]}
        activity={activityOf(p.live, s.id)}
        phase={p.live.phases[s.id]}
        now={now}
        forkOf={forkOf}
        forks={count > 0
          ? { count, expanded, groupId, onToggle: () => setForkOpen(s.id, !expanded) }
          : undefined}
        onToggleSelect={() => p.onToggleSelect(s.id)}
        onDraftChange={(value) => p.onDraftChange(s.id, value)}
        onDraftClear={() => p.onDraftClear(s.id)}
        onRename={(name) => p.onRenameSession(s.id, name)}
        onAutoTitle={() => p.onAutoTitle(s.id)}
        onDelete={() => p.onDeleteSession(s.id)}
      />
      {expanded && (
        <div
          id={groupId}
          role="group"
          aria-label={`Forks of ${title}`}
          className={`d-flex flex-column gap-1${indents ? ' border-start ms-2 ps-2' : ''}`}
        >
          {node.children.map((c) => (
            <SessionBranch
              key={c.session.id}
              node={c}
              forkOf={title}
              now={now}
              forkOpen={forkOpen}
              setForkOpen={setForkOpen}
              sidebar={p}
            />
          ))}
        </div>
      )}
    </>
  );
};

const SessionSidebar: React.FC<SessionSidebarProps> = (p) => {
  // A clock for each row's recency badge. The finest label counts seconds
  // ("30s"), but only for a session's first minute; ten-second ticks keep
  // that count honest without re-rendering the list every second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  // Only explicit choices are stored; a parent nobody has toggled follows
  // the running/selected default, so it opens when a fork starts working.
  const [forkOpen, setForkOpenState] = useState<Record<string, boolean>>({});
  const setForkOpen = (id: string, open: boolean) =>
    setForkOpenState((prev) => ({ ...prev, [id]: open }));

  // Fork trees per workspace. Built from each group's own list, so a fork
  // whose parent is gone — deleted, or in another workspace — is a root.
  const trees = useMemo(
    () => new Map(p.groups.map((g) => [g.workspace.id, buildSessionTree(g.sessions)])),
    [p.groups],
  );

  return (
    // Width is user-controlled via the divider that follows; flexShrink: 0
    // stops flex from squashing it.
    <div className="d-flex flex-column" style={{ width: p.width, flexShrink: 0, minHeight: 0 }}>
      <div className="d-flex align-items-center justify-content-end gap-2 p-2 border-bottom">
        <Button
          size="sm"
          variant="outline-light"
          onClick={() => p.onNewSession()}
          disabled={p.creating !== null}
          className="d-flex align-items-center gap-1"
          title="New session on a new workspace"
        >
          {p.creating === 'new' ? <Spinner animation="border" size="sm" /> : <BsPlusCircle />} New workspace
        </Button>
      </div>
      <div style={{ overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
        {p.groups.length === 0 ? (
          <div className="text-muted small p-3">No workspaces yet. Click New workspace to start one.</div>
        ) : (
          <Stack gap={3} className="p-3">
            {p.groups.map((g) => (
              <div key={g.workspace.id} className="d-flex flex-column gap-2">
                <WorkspaceHeader
                  workspace={g.workspace}
                  phase={p.live.phases[g.workspace.id]}
                  placeholder={g.placeholder}
                  sessionCount={g.sessions.length}
                  onRename={(name) => p.onRenameWorkspace(g.workspace.id, name)}
                  onNewSession={() => p.onNewSession(g.workspace.id)}
                  creating={p.creating === g.workspace.id}
                  onDelete={() => p.onDeleteWorkspace(g.workspace, g.sessions.length)}
                />
                {g.sessions.length === 0 && (
                  <div className="text-muted small ps-3">No sessions. Use + to start one here.</div>
                )}
                {/* Indented under the header: these share its machine.
                    One line per session, so a tight gap. */}
                <Stack gap={1} className="ps-3">
                  {(trees.get(g.workspace.id) ?? []).map((node) => (
                    <SessionBranch
                      key={node.session.id}
                      node={node}
                      now={now}
                      forkOpen={forkOpen}
                      setForkOpen={setForkOpen}
                      sidebar={p}
                    />
                  ))}
                </Stack>
              </div>
            ))}
          </Stack>
        )}
      </div>
    </div>
  );
};

export default SessionSidebar;
